import React, { useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import './AnaliseDados.css';

const money = (value) =>
  Number(value || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  });

const number = (value, digits = 2) =>
  Number(value || 0).toLocaleString('pt-BR', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits
  });

const toInputDate = (date) => {
  const d = new Date(date);
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
};

const today = toInputDate(new Date());

const defaultStart = (() => {
  const d = new Date();
  d.setDate(d.getDate() - 29);
  return toInputDate(d);
})();

const parseBucketDate = (bucket) => {
  if (!bucket) return null;

  const raw = String(bucket).trim();
  const datePart = raw.slice(0, 10);

  if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
    return new Date(`${datePart}T00:00:00Z`);
  }

  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDateShort = (date) =>
  new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    timeZone: 'UTC'
  }).format(date);

const formatMonthShort = (date) =>
  new Intl.DateTimeFormat('pt-BR', {
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC'
  }).format(date);

const formatBucketLabel = (bucket, title) => {
  const date = parseBucketDate(bucket);
  if (!date) return String(bucket);

  const normalizedTitle = String(title || '').toLowerCase();

  if (normalizedTitle.includes('mensal')) {
    return formatMonthShort(date);
  }

  if (normalizedTitle.includes('semanal')) {
    return `Semana de ${formatDateShort(date)}`;
  }

  return formatDateShort(date);
};

const getBucketSortValue = (bucket) => {
  const date = parseBucketDate(bucket);
  return date ? date.getTime() : 0;
};

const formatPeriodLabel = (inicio, fim) => {
  const d1 = parseBucketDate(inicio);
  const d2 = parseBucketDate(fim);

  if (!d1 || !d2) return `${inicio} até ${fim}`;

  const inicioFmt = new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'UTC'
  }).format(d1);

  const fimFmt = new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'UTC'
  }).format(d2);

  return `${inicioFmt} até ${fimFmt}`;
};

const parseDateTimeValue = (value) => {
  if (!value) return null;

  const raw = String(value).trim().replace(' ', 'T');
  const parsed = new Date(raw);

  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatHourMinute = (value) => {
  const date = parseDateTimeValue(value);
  if (!date) return '-';

  return new Intl.DateTimeFormat('pt-BR', {
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
};

// ── Helpers exclusivos do Cofre Digital (Banking) ──────────────────────────
const moneyLedger = (value) => {
  const n = Number(value || 0);
  const sign = n < 0 ? '−' : '';
  return `${sign}${Math.abs(n).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  })}`;
};

const AMBIENTE_LABEL = { production: 'Produção', sandbox: 'Sandbox' };

const PIX_STATUS_LABEL = {
  created: 'Aguardando pagamento',
  paid: 'Pagas',
  canceled: 'Canceladas',
  overdue: 'Vencidas',
  expired: 'Expiradas'
};

const PIX_STATUS_COLOR = {
  created: '#c9a227',
  paid: '#1fae7a',
  canceled: '#94a3b8',
  overdue: '#d92d20',
  expired: '#94a3b8'
};

const FALHA_LABEL = {
  saldo: 'saldo',
  extrato: 'extrato',
  cartoes: 'cartões',
  titulares: 'titulares',
  cobrancas_pix: 'cobranças Pix'
};

function BankingPanel({ banking, periodLabel }) {
  if (!banking) return null;

  if (!banking.conectado) {
    return (
      <div className="bk-vault bk-vault-empty">
        <div className="bk-vault-empty-icon" aria-hidden="true">
          <svg viewBox="0 0 48 48" width="34" height="34">
            <circle cx="24" cy="24" r="21" fill="none" stroke="currentColor" strokeWidth="2" opacity="0.35" />
            <circle cx="24" cy="24" r="7" fill="none" stroke="currentColor" strokeWidth="2" />
            <line x1="24" y1="17" x2="24" y2="12" stroke="currentColor" strokeWidth="2" />
          </svg>
        </div>
        <div>
          <h3>Cofre Digital ainda não conectado</h3>
          <p>
            Este tenant não tem uma Conta Digital Stark Bank ativa. Conecte a conta na tela
            de Conta Digital para que saldo, extrato, cartões e titulares apareçam aqui.
          </p>
        </div>
      </div>
    );
  }

  const saldo = banking.saldo?.amount ?? null;
  const extrato = banking.extrato || {};
  const entradas = Number(extrato.entradas || 0);
  const saidas = Number(extrato.saidas || 0);
  const liquido = Number(extrato.liquido || 0);
  const maxFluxo = Math.max(entradas, saidas, 1);
  const larguraEntradas = Math.min(100, (entradas / maxFluxo) * 100);
  const larguraSaidas = Math.min(100, (saidas / maxFluxo) * 100);

  const cartoes = banking.cartoes;
  const titulares = banking.titulares;
  const pix = banking.cobrancas_pix;
  const falhas = banking.falhas || [];

  const temFalha = (chave) => falhas.includes(chave);

  const pixEntries = pix?.por_status ? Object.entries(pix.por_status) : [];
  const pixTotalQtd = pixEntries.reduce((acc, [, v]) => acc + Number(v.quantidade || 0), 0);

  return (
    <div className="bk-vault">
      <div className="bk-vault-topline">
        <div className="bk-vault-eyebrow">
          <span className="bk-vault-dot" />
          Cofre Digital · Stark Bank
        </div>
        <div className="bk-vault-badges">
          <span className={`bk-env-badge bk-env-${banking.ambiente}`}>
            {AMBIENTE_LABEL[banking.ambiente] || banking.ambiente}
          </span>
          {falhas.length > 0 && (
            <span className="bk-warn-badge" title={falhas.map((f) => FALHA_LABEL[f] || f).join(', ')}>
              {falhas.length} indisponível(is)
            </span>
          )}
        </div>
      </div>

      <div className="bk-vault-main">
        <div className="bk-balance-block">
          <span className="bk-balance-label">Saldo atual</span>
          <strong className="bk-balance-value">
            {saldo === null ? (
              <span className="bk-balance-indisponivel">indisponível</span>
            ) : (
              moneyLedger(saldo)
            )}
          </strong>
          {temFalha('saldo') && <span className="bk-mini-warn">Não foi possível sincronizar agora.</span>}
        </div>

        <div className="bk-flow-block">
          <div className="bk-flow-header">
            <span>Fluxo do período</span>
            <span className="bk-flow-period">{periodLabel}</span>
          </div>

          {extrato && !temFalha('extrato') ? (
            <>
              <div className="bk-flow-scale">
                <div className="bk-flow-scale-track">
                  <div className="bk-flow-scale-fill bk-flow-in" style={{ width: `${larguraEntradas}%` }} />
                  <div className="bk-flow-scale-fill bk-flow-out" style={{ width: `${larguraSaidas}%` }} />
                </div>
              </div>
              <div className="bk-flow-values">
                <div className="bk-flow-value bk-flow-value-in">
                  <span className="bk-flow-tag">Entradas</span>
                  <strong>{moneyLedger(entradas)}</strong>
                </div>
                <div className="bk-flow-value bk-flow-value-net">
                  <span className="bk-flow-tag">Líquido</span>
                  <strong className={liquido >= 0 ? 'bk-positive' : 'bk-negative'}>
                    {moneyLedger(liquido)}
                  </strong>
                </div>
                <div className="bk-flow-value bk-flow-value-out">
                  <span className="bk-flow-tag">Saídas</span>
                  <strong>{moneyLedger(saidas)}</strong>
                </div>
              </div>
              {extrato.truncado && (
                <span className="bk-mini-warn">
                  Extrato com mais lançamentos do que o teto de leitura — números podem estar parciais.
                </span>
              )}
            </>
          ) : (
            <span className="bk-mini-warn">Extrato indisponível no momento.</span>
          )}
        </div>
      </div>

      <div className="bk-vault-grid">
        <div className="bk-tile">
          <div className="bk-tile-header">
            <span>Cartões corporativos</span>
          </div>

          {cartoes && !temFalha('cartoes') ? (
            <div className="bk-cards-row">
              <div className="bk-card-visual bk-card-physical">
                <span className="bk-card-chip" />
                <span className="bk-card-count">{cartoes.fisicos}</span>
                <span className="bk-card-caption">Físicos</span>
              </div>
              <div className="bk-card-visual bk-card-virtual">
                <span className="bk-card-chip" />
                <span className="bk-card-count">{cartoes.virtuais}</span>
                <span className="bk-card-caption">Virtuais</span>
              </div>
              <div className="bk-cards-stats">
                <div className="bk-stat-pill bk-stat-ok">
                  <strong>{cartoes.ativos}</strong>
                  <span>ativos</span>
                </div>
                <div className="bk-stat-pill bk-stat-warn">
                  <strong>{cartoes.bloqueados}</strong>
                  <span>bloqueados</span>
                </div>
                <div className="bk-stat-pill">
                  <strong>{cartoes.total}</strong>
                  <span>total</span>
                </div>
              </div>
            </div>
          ) : (
            <span className="bk-mini-warn">Cartões indisponíveis no momento.</span>
          )}
        </div>

        <div className="bk-tile">
          <div className="bk-tile-header">
            <span>Titulares</span>
          </div>

          {titulares && !temFalha('titulares') ? (
            <div className="bk-holders-block">
              <strong className="bk-holders-number">{titulares.total}</strong>
              <span className="bk-holders-caption">titular(es) na Stark Bank</span>
            </div>
          ) : (
            <span className="bk-mini-warn">Titulares indisponíveis no momento.</span>
          )}
        </div>

        <div className="bk-tile bk-tile-wide">
          <div className="bk-tile-header">
            <span>Cobranças Pix (aporte) no período</span>
            {pix && <span className="bk-tile-header-extra">{pixTotalQtd} cobrança(s)</span>}
          </div>

          {pix && !temFalha('cobrancas_pix') ? (
            <>
              <div className="bk-pix-totals">
                <div>
                  <span className="bk-pix-label">Gerado</span>
                  <strong>{money(pix.total_gerado)}</strong>
                </div>
                <div>
                  <span className="bk-pix-label">Pago</span>
                  <strong className="bk-positive">{money(pix.total_pago)}</strong>
                </div>
              </div>

              {pixEntries.length > 0 ? (
                <>
                  <div className="bk-pix-bar">
                    {pixEntries.map(([status, v]) => (
                      <div
                        key={status}
                        className="bk-pix-bar-seg"
                        style={{
                          width: `${(Number(v.quantidade || 0) / Math.max(1, pixTotalQtd)) * 100}%`,
                          background: PIX_STATUS_COLOR[status] || '#7a5af8'
                        }}
                        title={`${PIX_STATUS_LABEL[status] || status}: ${v.quantidade}`}
                      />
                    ))}
                  </div>
                  <div className="bk-pix-legend">
                    {pixEntries.map(([status, v]) => (
                      <span key={status} className="bk-pix-legend-item">
                        <i style={{ background: PIX_STATUS_COLOR[status] || '#7a5af8' }} />
                        {PIX_STATUS_LABEL[status] || status} ({v.quantidade})
                      </span>
                    ))}
                  </div>
                </>
              ) : (
                <span className="bk-mini-warn">Nenhuma cobrança gerada no período.</span>
              )}
            </>
          ) : (
            <span className="bk-mini-warn">Cobranças Pix indisponíveis no momento.</span>
          )}
        </div>
      </div>
    </div>
  );
}

function MiniBars({ title, seriesRealizado = [], seriesOperacional = [] }) {
  const merged = useMemo(() => {
    const map = new Map();

    seriesRealizado.forEach((item) => {
      map.set(item.bucket, {
        bucket: item.bucket,
        realizado: Number(item.total || 0),
        operacional: 0
      });
    });

    seriesOperacional.forEach((item) => {
      const current = map.get(item.bucket) || {
        bucket: item.bucket,
        realizado: 0,
        operacional: 0
      };
      current.operacional = Number(item.total || 0);
      map.set(item.bucket, current);
    });

    return [...map.values()].sort(
      (a, b) => getBucketSortValue(a.bucket) - getBucketSortValue(b.bucket)
    );
  }, [seriesRealizado, seriesOperacional]);

  const maxValue = Math.max(
    1,
    ...merged.map((item) => Math.max(item.realizado, item.operacional))
  );

  return (
    <div className="analytics-card analytics-card-chart">
      <div className="analytics-card-header">
        <h3>{title}</h3>
        <span>{merged.length} período(s)</span>
      </div>

      <div className="analytics-bars">
        {merged.length === 0 && (
          <div className="analytics-empty">Sem dados no período.</div>
        )}

        {merged.map((item) => {
          const label = formatBucketLabel(item.bucket, title);

          return (
            <div className="analytics-bar-row" key={item.bucket}>
              <div className="analytics-bar-label" title={String(item.bucket)}>
                {label}
              </div>

              <div className="analytics-bar-stack">
                <div
                  className="analytics-bar analytics-bar-realizado"
                  style={{ width: `${(item.realizado / maxValue) * 100}%` }}
                  title={`Realizado: ${money(item.realizado)}`}
                />
                <div
                  className="analytics-bar analytics-bar-operacional"
                  style={{ width: `${(item.operacional / maxValue) * 100}%` }}
                  title={`Operacional: ${money(item.operacional)}`}
                />
              </div>

              <div className="analytics-bar-values">
                <span>R {money(item.realizado)}</span>
                <span>O {money(item.operacional)}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SummaryCard({ label, value, extra, accent = 'blue' }) {
  return (
    <div className={`analytics-card analytics-summary analytics-accent-${accent}`}>
      <span className="analytics-summary-label">{label}</span>
      <strong className="analytics-summary-value">{value}</strong>
      {extra ? <span className="analytics-summary-extra">{extra}</span> : null}
    </div>
  );
}

export default function AnaliseDados() {
  const [filters, setFilters] = useState({
    dataInicio: defaultStart,
    dataFim: today
  });

  const [loading, setLoading] = useState(false);
  const [overview, setOverview] = useState(null);
  const [motoristas, setMotoristas] = useState(null);
  const [anomalias, setAnomalias] = useState(null);
  const [insight, setInsight] = useState('');
  const [sendingAlert, setSendingAlert] = useState(false);
  const [alertFeedback, setAlertFeedback] = useState('');
  const [hereBenchmark, setHereBenchmark] = useState(null);

  const gapAuditoria = Number(overview?.faturamento?.resumo?.gap_auditoria || 0);
  const providers = overview?.providers || {};

  const periodLabel = useMemo(
    () => formatPeriodLabel(filters.dataInicio, filters.dataFim),
    [filters.dataInicio, filters.dataFim]
  );

  const loadAll = async () => {
    setLoading(true);
    try {
      const params = {
        dataInicio: filters.dataInicio,
        dataFim: filters.dataFim
      };

      const [overviewRes, motoristasRes, anomaliasRes] = await Promise.all([
        api.get('/api/analytics/overview', { params }),
        api.get('/api/analytics/motoristas', { params }),
        api.get('/api/analytics/anomalias', { params })
      ]);

      setOverview(overviewRes.data);
      setMotoristas(motoristasRes.data);
      setAnomalias(anomaliasRes.data);
    } catch (error) {
      console.error(error);
      window.alert(error.response?.data?.error || 'Erro ao carregar analytics.');
    } finally {
      setLoading(false);
    }
  };

  const loadInsight = async () => {
    try {
      const { data } = await api.post('/api/analytics/insights', filters);
      setInsight(data.analysis || '');
    } catch (error) {
      console.error(error);
      window.alert(error.response?.data?.error || 'Erro ao gerar insight com IA.');
    }
  };

  const loadHereBenchmark = async () => {
    try {
      const { data } = await api.post('/api/analytics/external/here/benchmark', {
        data: filters.dataFim
      });
      setHereBenchmark(data);
    } catch (error) {
      console.error(error);
      window.alert(error.response?.data?.error || 'Erro ao consultar benchmark externo.');
    }
  };

  const sendAuditAlert = async () => {
    setSendingAlert(true);
    setAlertFeedback('');

    try {
      const payload = {
        canal: providers.email ? 'email' : providers.sms ? 'sms' : 'whatsapp',
        destino: providers.email ? 'auditoria@blbsystem.local' : '11999999999',
        assunto: 'Alerta analítico automático',
        mensagem: `Gap de auditoria atual: ${money(gapAuditoria)} | Alertas: ${anomalias?.resumo?.total_alertas || 0}`,
        html: `
          <h3>Radar Analytics BLB</h3>
          <p><strong>Período:</strong> ${periodLabel}</p>
          <p><strong>Gap de auditoria:</strong> ${money(gapAuditoria)}</p>
          <p><strong>Total de alertas:</strong> ${anomalias?.resumo?.total_alertas || 0}</p>
        `
      };

      const { data } = await api.post('/api/analytics/alerts/dispatch', payload);
      setAlertFeedback(data.message || 'Alerta enviado com sucesso.');
    } catch (error) {
      console.error(error);
      setAlertFeedback(error.response?.data?.error || 'Falha ao enviar alerta.');
    } finally {
      setSendingAlert(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const topMotoristas = useMemo(() => {
    const list = motoristas?.motoristas || [];
    return [...list]
      .sort((a, b) => b.score_auditoria - a.score_auditoria)
      .slice(0, 5);
  }, [motoristas]);

  return (
    <div className="analytics-page">
      <div className="analytics-page-header">
        <div>
          <h1>Central Analítica & Auditoria</h1>
          <p>
            Consolidação firme de faturamento, desempenho operacional e desvios auditáveis.
            Aqui nada pode escapar do radar.
          </p>
          <div className="analytics-period-chip">
            Período analisado: <strong>{periodLabel}</strong>
          </div>
        </div>

        <div className="analytics-toolbar">
          <div className="analytics-filter-group">
            <label>Início</label>
            <input
              type="date"
              value={filters.dataInicio}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, dataInicio: e.target.value }))
              }
            />
          </div>

          <div className="analytics-filter-group">
            <label>Fim</label>
            <input
              type="date"
              value={filters.dataFim}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, dataFim: e.target.value }))
              }
            />
          </div>

          <button className="analytics-btn primary" onClick={loadAll} disabled={loading}>
            {loading ? 'Atualizando...' : 'Atualizar painel'}
          </button>

          <button className="analytics-btn ghost" onClick={loadInsight}>
            Parecer IA
          </button>

          <button
            className="analytics-btn ghost"
            onClick={loadHereBenchmark}
            disabled={!providers.here}
          >
            Benchmark HERE
          </button>
        </div>
      </div>

      <div className="analytics-provider-strip">
        <span className={providers.sms ? 'ok' : 'off'}>SMS / Comtele</span>
        <span className={providers.email ? 'ok' : 'off'}>E-mail / Brevo</span>
        <span className={providers.whats ? 'ok' : 'off'}>WhatsApp</span>
        <span className={providers.gemini ? 'ok' : 'off'}>Gemini IA</span>
        <span className={providers.here ? 'ok' : 'off'}>HERE Traffic / Matrix</span>
      </div>

      <div className="analytics-summary-grid">
        <SummaryCard
          label="Faturamento Realizado"
          value={money(overview?.faturamento?.resumo?.faturamento_realizado)}
          accent="green"
        />
        <SummaryCard
          label="Faturamento Operacional"
          value={money(overview?.faturamento?.resumo?.faturamento_operacional)}
          accent="blue"
        />
        <SummaryCard
          label="Gap de Auditoria"
          value={money(gapAuditoria)}
          extra="Operacional - Realizado"
          accent={gapAuditoria > 0 ? 'orange' : 'purple'}
        />
        <SummaryCard
          label="Total de Alertas"
          value={String(anomalias?.resumo?.total_alertas || 0)}
          extra="Itens que precisam de ação"
          accent={(anomalias?.resumo?.total_alertas || 0) > 0 ? 'red' : 'green'}
        />
      </div>

      <BankingPanel banking={overview?.banking} periodLabel={periodLabel} />

      <div className="analytics-grid analytics-grid-2">
        <MiniBars
          title="Faturamento Diário"
          seriesRealizado={overview?.faturamento?.diario?.realizado || []}
          seriesOperacional={overview?.faturamento?.diario?.operacional || []}
        />
        <MiniBars
          title="Faturamento Semanal"
          seriesRealizado={overview?.faturamento?.semanal?.realizado || []}
          seriesOperacional={overview?.faturamento?.semanal?.operacional || []}
        />
      </div>

      <div className="analytics-grid analytics-grid-1">
        <MiniBars
          title="Faturamento Mensal"
          seriesRealizado={overview?.faturamento?.mensal?.realizado || []}
          seriesOperacional={overview?.faturamento?.mensal?.operacional || []}
        />
      </div>

      <div className="analytics-grid analytics-grid-2">
        <div className="analytics-card">
          <div className="analytics-card-header">
            <h3>Desempenho dos motoristas</h3>
            <span>{motoristas?.resumo?.motoristas_monitorados || 0} monitorados</span>
          </div>

          <div className="analytics-table-wrap">
            <table className="analytics-table">
              <thead>
                <tr>
                  <th>Motorista</th>
                  <th>OS fechadas</th>
                  <th>Produtividade</th>
                  <th>KM rodado</th>
                  <th>Manutenções</th>
                  <th>Receita</th>
                  <th>Score</th>
                </tr>
              </thead>
              <tbody>
                {(motoristas?.motoristas || []).map((item) => (
                  <tr key={item.id}>
                    <td>
                      <div className="analytics-driver-name">
                        <strong>{item.nome}</strong>
                        <span className={`status-pill ${item.status_operacional || 'offline'}`}>
                          {item.status_operacional || 'offline'}
                        </span>
                      </div>
                    </td>
                    <td>{item.os_fechadas}</td>
                    <td>{number(item.produtividade_media_dia)}</td>
                    <td>{number(item.km_rodado)} km</td>
                    <td>{item.manutencoes_associadas}</td>
                    <td>{money(item.receita_realizada)}</td>
                    <td>{number(item.score_auditoria)}</td>
                  </tr>
                ))}

                {(!motoristas?.motoristas || motoristas.motoristas.length === 0) && (
                  <tr>
                    <td colSpan="7" className="analytics-empty">
                      Nenhum motorista encontrado no período.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="analytics-card">
          <div className="analytics-card-header">
            <h3>Top 5 por score de auditoria</h3>
            <button className="analytics-btn small" onClick={sendAuditAlert} disabled={sendingAlert}>
              {sendingAlert ? 'Enviando...' : 'Disparar alerta'}
            </button>
          </div>

          <div className="analytics-ranking">
            {topMotoristas.map((item, idx) => (
              <div className="analytics-ranking-item" key={item.id}>
                <div className="analytics-ranking-index">#{idx + 1}</div>
                <div className="analytics-ranking-content">
                  <strong>{item.nome}</strong>
                  <span>
                    {item.os_fechadas} OS | {number(item.km_rodado)} km | Score {number(item.score_auditoria)}
                  </span>
                </div>
              </div>
            ))}
            {topMotoristas.length === 0 && (
              <div className="analytics-empty">Sem ranking disponível.</div>
            )}
          </div>

          {alertFeedback ? <div className="analytics-feedback">{alertFeedback}</div> : null}

          <div className="analytics-insights-note">
            Score composto por OS fechadas, produtividade média por dia, KM rodado e manutenções associadas.
          </div>
        </div>
      </div>

      <div className="analytics-grid analytics-grid-2">
        <div className="analytics-card">
          <div className="analytics-card-header">
            <h3>Anomalias auditáveis</h3>
            <span>{anomalias?.resumo?.total_alertas || 0} alertas</span>
          </div>

          <div className="analytics-anomaly-grid">
            <div className="anomaly-box">
              <strong>OS sem lançamento financeiro</strong>
              <span>{anomalias?.resumo?.os_sem_financeiro || 0}</span>
            </div>
            <div className="anomaly-box">
              <strong>Financeiro sem OS</strong>
              <span>{anomalias?.resumo?.financeiro_sem_os || 0}</span>
            </div>
            <div className="anomaly-box">
              <strong>Motoristas sem GPS</strong>
              <span>{anomalias?.resumo?.motoristas_sem_gps || 0}</span>
            </div>
            <div className="anomaly-box">
              <strong>Manutenções sem espelho</strong>
              <span>{anomalias?.resumo?.manutencoes_sem_espelho || 0}</span>
            </div>
          </div>

          <div className="analytics-table-wrap compact">
            <table className="analytics-table">
              <thead>
                <tr>
                  <th>Tipo</th>
                  <th>Referência</th>
                  <th>Detalhe</th>
                </tr>
              </thead>
              <tbody>
                {(anomalias?.os_sem_financeiro || []).slice(0, 5).map((item) => (
                  <tr key={`os-${item.id}`}>
                    <td>OS sem financeiro</td>
                    <td>{item.id}</td>
                    <td>{money(item.valor)} | motorista {item.motorista_id || 'N/A'}</td>
                  </tr>
                ))}

                {(anomalias?.financeiro_sem_os || []).slice(0, 5).map((item) => (
                  <tr key={`fin-${item.id}`}>
                    <td>Financeiro sem OS</td>
                    <td>{item.os_id}</td>
                    <td>{item.descricao}</td>
                  </tr>
                ))}

                {(anomalias?.motoristas_sem_gps || []).slice(0, 5).map((item) => (
                  <tr key={`gps-${item.id}`}>
                    <td>Motorista sem GPS</td>
                    <td>{item.nome}</td>
                    <td>{item.os_fechadas} OS fechadas no período</td>
                  </tr>
                ))}

                {(anomalias?.manutencoes_sem_espelho || []).slice(0, 5).map((item) => (
                  <tr key={`man-${item.id}`}>
                    <td>Manutenção sem espelho</td>
                    <td>{item.id}</td>
                    <td>{item.descricao}</td>
                  </tr>
                ))}

                {((anomalias?.resumo?.total_alertas || 0) === 0) && (
                  <tr>
                    <td colSpan="3" className="analytics-empty">
                      Nenhuma anomalia identificada.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="analytics-card">
          <div className="analytics-card-header">
            <h3>Parecer IA / Auditoria executiva</h3>
            <span>Gemini</span>
          </div>

          {insight ? (
            <div
              className="analytics-ia"
              dangerouslySetInnerHTML={{ __html: insight }}
            />
          ) : (
            <div className="analytics-empty">
              Clique em <strong>Parecer IA</strong> para gerar uma leitura executiva do período.
            </div>
          )}
        </div>
      </div>

      <div className="analytics-grid analytics-grid-1">
        <div className="analytics-card">
          <div className="analytics-card-header">
            <h3>Enriquecimento externo: HERE Traffic / Matrix</h3>
            <span>{providers.here ? 'Configurado' : 'Não configurado'}</span>
          </div>

          {!providers.here && (
            <div className="analytics-empty">
              Configure <strong>HERE_API_KEY</strong> no backend para habilitar benchmark externo de rota estimada x rota percorrida.
            </div>
          )}

          {providers.here && hereBenchmark && (
  <div className="analytics-table-wrap">
    <table className="analytics-table">
      <thead>
        <tr>
          <th>Motorista / ID</th>
          <th>KM interno</th>
          <th>KM HERE</th>
          <th>Tempo HERE</th>
          <th>Desvio</th>
          <th>Coleta do dia</th>
        </tr>
      </thead>
      <tbody>
        {(hereBenchmark.comparativos || []).map((item) => {
          const primeiro = formatHourMinute(item.primeiro_ponto_em);
          const ultimo = formatHourMinute(item.ultimo_ponto_em);

          return (
            <tr key={`here-${item.motorista_id}`}>
              <td>
                <div className="analytics-driver-name">
                  <strong>{item.motorista_nome || `Motorista ${item.motorista_id}`}</strong>
                  <span>ID {item.motorista_id}</span>
                </div>
              </td>

              <td>{number(item.km_interno)} km</td>

              <td>
                {item.km_here_estimado == null
                  ? '-'
                  : `${number(item.km_here_estimado)} km`}
              </td>

              <td>
                {item.tempo_here_estimado_min == null
                  ? '-'
                  : `${number(item.tempo_here_estimado_min, 1)} min`}
              </td>

              <td>
                {item.desvio_pct == null
                  ? '-'
                  : `${number(item.desvio_pct, 2)}%`}
              </td>

              <td>
                <div className="analytics-driver-name">
                  <strong>{primeiro} → {ultimo}</strong>
                  <span>{item.total_pontos || 0} ponto(s)</span>
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
)}

          {providers.here && !hereBenchmark && (
            <div className="analytics-empty">
              Clique em <strong>Benchmark HERE</strong> para comparar rota interna x rota estimada por tráfego.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}